import { FC, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useS3ImageUploader } from 'hooks/useS3ImageUploader';
import { Button } from 'components/Core/Button';
import { Switch } from 'components/Core/Switch/Switch';
import { ImageUploader } from 'components/Core/ImageUploader/ImageUploader';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { PopularProductsTableTransFields } from 'components/Tables/PopularProductsTable/types';
import { popularProductsTableTranslations } from 'components/Tables/PopularProductsTable/translations';
import { IPopularProductData } from 'stores/popularProducts/types';
import { defaultValues } from './defaults';
import { popularProductFormTranslations } from './translations';
import { IPopularProductFormWithFiles, PopularProductFormTransFields } from './types';
import styles from './popularProductForm.module.scss';
import { namePattern } from 'utils/patterns';

export const PopularProductForm: FC = observer(() => {
  const {
    popularProductsStore: { popularProductData, editPopularProduct, addPopularProduct, deletePopularProduct },
    alertStore: { showAlert },
    popUpStore: { openPopUp, closePopUp },
    mainStore: { user },
  } = useStore();

  const { t } = useMyTranslations<PopularProductFormTransFields | PopularProductsTableTransFields>([
    popularProductFormTranslations.name,
    popularProductsTableTranslations.name,
  ]);

  const navigate = useNavigate();

  const { mutate, isLoading } = useMutation(popularProductData ? editPopularProduct : addPopularProduct, {
    mutationKey: 'addPopularProduct-editPopularProduct',
    onSuccess: (res: IPopularProductData | null) => {
      showAlert(t(res ? 'createSuccess' : 'editSuccess'), 'success');
      res && navigate(`/popular-products/${res.id}`);
    },
  });

  const { mutate: deletePopularProductMutation, isLoading: isLoadingDeleting } = useMutation(deletePopularProduct, {
    mutationKey: 'deletePopularProduct',
    onSuccess: () => {
      closePopUp();
      showAlert(t('deleteSuccess', { ns: popularProductsTableTranslations.name }), 'success');
      navigate(`/popular-products`, { replace: false });
    },
  });

  const useFormMethods = useForm<IPopularProductFormWithFiles>({
    defaultValues: popularProductData || defaultValues,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useFormMethods;

  const { uploadImage, isLoading: isImageUploading } = useS3ImageUploader<IPopularProductFormWithFiles>({ setValue });

  const onSubmitHandler = useCallback(async (inputData: IPopularProductFormWithFiles) => {
    try {
      const { imageUrlFile: file } = inputData;
      if (file)
        await uploadImage(file, 'imageUrl', `${user?.selectedCountryCode.countryCode.toLowerCase()}/popular-products`);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { imageUrlFile, ...data } = getValues();
      mutate(data);
    } catch {
      showAlert('Problems with image upload', 'warning');
    }
  }, []);

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.leftSection}`}>
            <div className={`${styles.formSection}`}>
              <ControlledInput
                name='name'
                type='text'
                required
                label={t('name')}
                maxLength={30}
                registerPattern={namePattern}
              />
              {popularProductData && (
                <div className={styles.checkboxWrap}>
                  <h6 className={`${styles.title}`}>{t('status')}</h6>
                  <Switch
                    isActive={watch('status') === 'Active'}
                    onChange={() => setValue('status', getValues('status') === 'Active' ? 'Inactive' : 'Active')}
                  />
                </div>
              )}
            </div>
            <div className={styles.bottomSection}>
              <Button
                title={t('save')}
                onClick={handleSubmit(onSubmitHandler)}
                isDisabled={isImageUploading || isSubmitting || isLoading || isLoadingDeleting}
              />
              {popularProductData && (
                <Button
                  title={t('confirm', { ns: popularProductsTableTranslations.name })}
                  onClick={() =>
                    openPopUp({
                      title: t('confirmationTitle', { ns: popularProductsTableTranslations.name }),
                      description: t('approve-text', { ns: popularProductsTableTranslations.name }),
                      buttonLabel: t('confirm', { ns: popularProductsTableTranslations.name }),
                      formName: 'confirmation',
                      cb: () => deletePopularProductMutation(popularProductData.id),
                    })
                  }
                  isDisabled={isImageUploading || isSubmitting || isLoading || isLoadingDeleting}
                  classNameProp={styles.deleteBtn}
                />
              )}
            </div>
          </section>
          <section className={styles.rightSection}>
            <ImageUploader name='imageUrl' title={t('imageUrl')} required path='popular-products' />
          </section>
        </form>
      </div>
    </FormProvider>
  );
});
